import { useTranslations } from "next-intl";
import { Spinner } from "@client/components/ui/Spinner";
import { useAppDispatch, useAppSelector } from "@client/store/store.hooks";
import { User, Mail, MonitorUp, AlertTriangle, XCircle } from "lucide-react";
import {
  setUsername,
  selectUsername,
  selectIsJoining,
  selectIsGuestMode,
  selectJoinRejection,
  selectIsNameEditable,
  selectGuestEmail,
  selectIsEmailEditable,
  setGuestEmail
} from "@client/store/slices/preJoin.slice";

interface JoinFormProps {
  // Title and description
  title: string;
  description: string;
  // User info
  userLabel: string;
  hideNameInput: boolean;
  // Actions
  joinLabel: string;
  hidePresentButton: boolean;
  onSubmit: (shouldPresent: boolean) => void;
  // Children
  children?: React.ReactNode;
}

/**
 * Join form with name input and action buttons.
 */
export const JoinForm = ({
  title,
  description,
  userLabel,
  hideNameInput,
  joinLabel,
  hidePresentButton,
  onSubmit,
  children
}: JoinFormProps) => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const username = useAppSelector(selectUsername);
  const isJoining = useAppSelector(selectIsJoining);
  const isGuestMode = useAppSelector(selectIsGuestMode);
  const isNameEditable = useAppSelector(selectIsNameEditable);
  const guestEmail = useAppSelector(selectGuestEmail);
  const isEmailEditable = useAppSelector(selectIsEmailEditable);
  const { isRejected, rejectedMessage } = useAppSelector(selectJoinRejection);

  const hasValidGuestEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail.trim());
  const isButtonDisabled =
    (!hideNameInput && username.length < 3) || (isGuestMode && !hasValidGuestEmail) || isJoining || isRejected;

  let disabledReason: string | undefined;
  if (isRejected && rejectedMessage) {
    disabledReason = rejectedMessage;
  } else if (!hideNameInput && username.length < 3) {
    disabledReason = t("validation.nameTooShort") || "Name must be at least 3 characters";
  } else if (isGuestMode && !hasValidGuestEmail) {
    disabledReason = t("validation.emailInvalid");
  } else if (isJoining) {
    disabledReason = t("preJoin.connecting") || "Joining in progress";
  }

  const handleSubmit = (e: React.FormEvent, shouldPresent: boolean) => {
    e.preventDefault();
    if (!isJoining) {
      onSubmit(shouldPresent);
    }
  };

  const onUsernameChange = (value: string) => {
    dispatch(setUsername(value));
  };

  const onGuestEmailChange = (value: string) => {
    dispatch(setGuestEmail(value));
  };

  return (
    <>
      <div className="text-center lg:text-left mb-3 sm:mb-10">
        <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-1 sm:mb-2 tracking-tight">
          {title}
        </h1>

        {/* Security Warning */}
        {typeof window !== "undefined" && !window.isSecureContext && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2 sm:gap-3">
            <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-amber-800 text-xs sm:text-sm">
              <p className="font-semibold mb-1">{t("preJoin.browserSecurityRestriction")}</p>
              <p>
                {t("preJoin.cameraAndMicBlocked")}
                <br />
                {t("preJoin.toFixThis")}
                <ul className="list-disc ml-4 mt-2 mb-1">
                  <li>
                    {t("preJoin.accessViaLocalhost")} <strong>http://localhost:4000</strong>
                  </li>
                  <li>{t("preJoin.setupHTTPS")}</li>
                </ul>
              </p>
            </div>
          </div>
        )}

        {description && <p className="text-gray-500 text-xs sm:text-base md:text-lg pb-4">{description}</p>}

        {/* Rejected Message */}
        {isRejected && rejectedMessage && (
          <div className="mt-4 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl shadow-sm animate-in fade-in slide-in-from-top-2 duration-300">
            <XCircle className="h-5 w-5 text-red-500 shrink-0" />
            <span className="font-medium text-sm">{rejectedMessage}</span>
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => handleSubmit(e, false)}
        className="flex flex-col gap-3 sm:gap-6 w-full max-w-sm mx-auto lg:mx-0"
      >
        <div className="space-y-3 sm:space-y-4">
          {!hideNameInput && (
            <>
              {isGuestMode && isNameEditable && (
                <div className="flex items-center gap-2 mb-3 sm:mb-6 px-3 py-2 bg-amber-50 text-amber-900 rounded-lg border border-amber-200 shadow-sm text-xs sm:text-sm">
                  <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600 shrink-0" />
                  <span className="font-medium">{t("preJoin.enterAccurateName")}</span>
                </div>
              )}
              <div className="relative group">
                <div className="absolute inset-y-0 start-0 ps-3 sm:ps-4 flex items-center pointer-events-none">
                  <User className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-gray-400 group-focus-within:text-primary transition-colors" />
                </div>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => onUsernameChange(e.target.value)}
                  className={`block w-full ps-10 sm:ps-11 md:ps-12 pe-3 sm:pe-4 py-3 sm:py-4 md:py-5 border border-gray-200 rounded-xl sm:rounded-2xl leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-gray-900 text-sm sm:text-base md:text-lg transition-all duration-200 shadow-sm group-hover:border-gray-300 ${
                    !isNameEditable ? "cursor-not-allowed opacity-75 bg-gray-50 text-gray-500" : ""
                  }`}
                  placeholder={isGuestMode ? t("preJoin.enterName") : t("preJoin.namePlaceholder")}
                  required={!hideNameInput}
                  disabled={isJoining || !isNameEditable}
                  readOnly={!isNameEditable}
                  autoFocus={isGuestMode && !username}
                  minLength={3}
                  maxLength={18}
                />
                <label
                  htmlFor="username"
                  className="absolute -top-2 sm:-top-2.5 md:-top-3 start-3 bg-white px-1.5 sm:px-2 text-xs md:text-sm font-bold text-gray-500 group-focus-within:text-primary transition-colors rounded-full"
                >
                  {userLabel}
                </label>
              </div>
            </>
          )}
          {isGuestMode && (
            <div className="relative group">
              <div className="absolute inset-y-0 start-0 ps-3 sm:ps-4 flex items-center pointer-events-none">
                <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 group-focus-within:text-primary transition-colors" />
              </div>
              <input
                type="email"
                id="guest-email"
                value={guestEmail}
                onChange={(e) => onGuestEmailChange(e.target.value)}
                className={`block w-full ps-10 sm:ps-11 pe-3 sm:pe-4 py-3 sm:py-4 border border-gray-200 rounded-xl sm:rounded-2xl leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-gray-900 text-sm sm:text-base transition-all duration-200 shadow-sm group-hover:border-gray-300 ${
                  !isEmailEditable ? "cursor-not-allowed opacity-75 bg-gray-50 text-gray-500" : ""
                }`}
                placeholder={t("preJoin.emailPlaceholder")}
                autoComplete="email"
                required
                disabled={isJoining || !isEmailEditable}
                readOnly={!isEmailEditable}
                maxLength={254}
              />
              <label
                htmlFor="guest-email"
                className="absolute -top-2 sm:-top-2.5 start-3 bg-white px-1.5 sm:px-2 text-xs sm:text-sm font-bold text-gray-500 group-focus-within:text-primary transition-colors rounded-full"
              >
                {t("preJoin.emailLabel")}
              </label>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2.5 sm:gap-3.5 pb-8 xl:pb-0">
          {children}
          <button
            type="submit"
            disabled={isButtonDisabled}
            title={isButtonDisabled && disabledReason ? disabledReason : undefined}
            className="w-full flex items-center justify-center px-4 sm:px-8 py-2.5 sm:py-3.5 md:py-4 border border-transparent text-sm sm:text-base md:text-lg font-bold rounded-xl sm:rounded-2xl text-white bg-primary hover:bg-primary-hover focus:outline-none focus:ring-4 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98] active:translate-y-0"
          >
            {isJoining ? (
              <div className="flex items-center gap-2">
                <Spinner className="w-4 h-4 text-white" />
                <span>{t("preJoin.connecting")}</span>
              </div>
            ) : (
              joinLabel
            )}
          </button>

          {!hidePresentButton && (
            <button
              type="button"
              onClick={(e) => handleSubmit(e, true)}
              disabled={isButtonDisabled}
              title={isButtonDisabled && disabledReason ? disabledReason : undefined}
              className="hidden lg:flex w-full items-center justify-center gap-2 px-6 py-3 md:py-4 border border-gray-200 rounded-2xl text-sm md:text-base font-semibold text-gray-600 bg-white hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-100 transition-all active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MonitorUp className="w-4 h-4 md:w-5 md:h-5" />
              {t("preJoin.presentToRoom")}
            </button>
          )}

          {isButtonDisabled && disabledReason && (
            <div className="mt-2 text-center lg:text-left">
              <span className="text-xs text-gray-500">{disabledReason}</span>
            </div>
          )}
        </div>
      </form>
    </>
  );
};
